import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Calendar as CalendarIcon,
    Clock,
    MapPin,
    Users,
    DollarSign,
    Settings,
    Eye,
    X,
    Zap,
    Target,
    Heart,
    Trophy,
    Gamepad2,
    Mountain,
    Waves,
    Dumbbell,
    TreePine,
    Music,
    Palette,
    Book,
    Coffee,
    Camera,
    Mic,
    Building,
    Utensils,
    Users2,
    Briefcase,
    PartyPopper,
    Plane,
    GraduationCap,
    Bike
} from 'lucide-react';
import { ActivityDifficulty, ActivityFormData, ActivityType, defaultActivityFormData } from '../types/activity';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ImageUpload } from './ui/image-upload';
import { LocationPicker } from './ui/location/location-picker';
import { LocationFeature, Location } from '../types/location';


// Activity types with icons and images
const activityTypes = [
    {
        id: ActivityType.SPORTS_FITNESS,
        name: 'Sports & Fitness',
        icon: Dumbbell,
        image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
        color: 'bg-green-500',
        description: 'Physical activities, sports, and fitness sessions'
    },
    {
        id: ActivityType.SOCIAL,
        name: 'Social',
        icon: Users2,
        image: 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=300&h=200&fit=crop',
        color: 'bg-emerald-500',
        description: 'Social gatherings, parties, dinners, and meetups'
    },
    {
        id: ActivityType.LEARNING,
        name: 'Learning',
        icon: GraduationCap,
        image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=300&h=200&fit=crop',
        color: 'bg-indigo-500',
        description: 'Workshops, conferences, classes, and educational events'
    },
    {
        id: ActivityType.ENTERTAINMENT,
        name: 'Entertainment',
        icon: Mic,
        image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
        color: 'bg-amber-500',
        description: 'Concerts, shows, festivals, and entertainment events'
    },
    {
        id: ActivityType.WELLNESS,
        name: 'Wellness',
        icon: Heart,
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=200&fit=crop',
        color: 'bg-pink-500',
        description: 'Yoga, meditation, spa, and wellness activities'
    },
    {
        id: ActivityType.TRAVEL,
        name: 'Travel',
        icon: Plane,
        image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=300&h=200&fit=crop',
        color: 'bg-violet-500',
        description: 'Travel experiences, tours, and exploration activities'
    },
    {
        id: ActivityType.OTHER,
        name: 'Other',
        icon: Settings,
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=200&fit=crop',
        color: 'bg-slate-500',
        description: 'Other activities that don\'t fit standard categories'
    }
];

// Difficulty levels
const difficultyLevels = [
    { id: 'Beginner', name: 'Beginner', description: 'No experience required', color: 'bg-green-100 text-green-800' },
    { id: 'Intermediate', name: 'Intermediate', description: 'Some experience helpful', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'Advanced', name: 'Advanced', description: 'Experience required', color: 'bg-red-100 text-red-800' },
    // { id: 'expert', name: 'Expert', description: 'Professional level', color: 'bg-purple-100 text-purple-800' }
];

// Popular locations (coordinates in [lng, lat] format for MapLibre)
const popularLocations = [
    { name: 'West LA', address: 'West LA, CA', type: 'park', coordinates: [-118.4085, 34.0522] },
    { name: 'Downtown', address: 'Downtown, CA', type: 'sports', coordinates: [-118.2437, 34.0522] },
    { name: 'Alhambra', address: 'Alhambra, CA', type: 'indoor', coordinates: [-118.1270, 34.0953] },
    { name: 'Irvine', address: 'Irvine, CA', type: 'wellness', coordinates: [-117.8265, 33.6846] },
];

// Quick time slots
const quickTimeSlots = [
    { time: '06:00', label: 'Early Morning', period: 'AM' },
    { time: '09:00', label: 'Morning', period: 'AM' },
    { time: '12:00', label: 'Lunch Time', period: 'PM' },
    { time: '15:00', label: 'Afternoon', period: 'PM' },
    { time: '18:00', label: 'Evening', period: 'PM' },
    { time: '20:00', label: 'Night', period: 'PM' }
];


const steps = [
    { id: 1, title: 'Activity Type', description: 'What kind of activity?' },
    { id: 2, title: 'Basic Info', description: 'Title and description' },
    { id: 3, title: 'Schedule', description: 'When will it happen?' },
    { id: 4, title: 'Location', description: 'Where will it take place?' },
    { id: 5, title: 'Details', description: 'Participants and difficulty' },
    { id: 6, title: 'Pricing', description: 'Set your price' },
    { id: 7, title: 'Review', description: 'Review and create' }
];

export function CreateActivityWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(1);
    const [isCreating, setIsCreating] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const currentDate = new Date().toISOString().split('T')[0];
    const currentTime = new Date().toISOString().split('T')[1];
    const UINewActivityFormData = { ...defaultActivityFormData, date: currentDate, time: currentTime };
    const [formData, setFormData] = useState(UINewActivityFormData);
    const [activityImage, setActivityImage] = useState<string | null>(null);

    const [selectedLocationFeature, setSelectedLocationFeature] = useState<LocationFeature | null>(null);
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [isCreatingLocation, setIsCreatingLocation] = useState(false);

    const [activityData, setActivityData] = useState<ActivityFormData>({
        type: '' as ActivityType,
        title: '',
        description: '',
        date: undefined,
        time: '',
        dateTime: new Date(),

        selectedLocationFeature: undefined,
        locationDisplayName: '',

        image: '',
        durationMinutes: 60,

        maxParticipants: 10,
        difficulty: ActivityDifficulty.BEGINNER,
        price: 0,
        isFree: true,
        // equipmentProvided: false,
        // requirements: ''
    });

    const handleLocationSelect = (locationFeature: LocationFeature) => {
        setSelectedLocationFeature(locationFeature);

        // store LocationFeature for backend to handle
        updateData({
            selectedLocationFeature: locationFeature,
            locationDisplayName: locationFeature.place_name,
        })
    }

    const handleActivityImageUpload = async (file: File) => {
        const uploadedResult = await apiService.uploadImage(file, 'ACTIVITY_IMAGE');
        return uploadedResult;
    }

    const handleActivityImageUploadSuccess = (result: any) => {
        // console.log(result);
        setActivityData(prev => ({ ...prev, image: result.image.cdnUrl }));
    }

    const updateData = (updates: Partial<ActivityFormData>) => {
        setActivityData(prev => ({ ...prev, ...updates }));
    };

    const nextStep = () => {
        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1: return activityData.type !== '' as ActivityType;
            case 2: return activityData.title.trim() !== '' && activityData.description.trim() !== '';
            case 3: return activityData.date && activityData.time !== '';
            // case 4: return activityData.location !== undefined; // || activityData.customLocation.trim() !== '';
            case 4: return selectedLocationFeature !== undefined;
            case 5: return activityData.maxParticipants > 0;
            case 6: return true;
            default: return true;
        }
    };

    const handleCreate = async () => {
        setIsCreating(true);
        // TODO: this is very messy, we need to have a better way to handle this
        // Call the api service to create the activity on the backend
        const {
            isFree,
            date,
            time,
            selectedLocationFeature,
            locationDisplayName,
            ...activityDataWithoutIsFree
        } = activityData;

        // if user didn't upload an image, use the default image for each type
        const defaultImage = activityTypes.find(type => type.id === activityData.type)?.image;

        // activityDataWithoutIsFree.dateTime = new Date(`${date}T${time}`);

        const finalData = {
            ...activityDataWithoutIsFree,
            image: activityDataWithoutIsFree.image || defaultImage || '',
            dateTime: new Date(`${date}T${time}`),
            locationFeature: selectedLocationFeature,
        }
        // console.log(finalData);
        await apiService.createActivity(finalData, user!.id);
        toast.success('Activity created successfully');
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));
        navigate('/explore');
    };

    const selectedActivityType = activityTypes.find(type => type.id === activityData.type);

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Choose Activity Type</h2>
                            <p className="text-muted-foreground">Select the type of activity you want to create</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {activityTypes.map((type) => {
                                const IconComponent = type.icon;
                                return (
                                    <Card
                                        key={type.id}
                                        className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${activityData.type === type.id ? 'ring-2 ring-primary' : ''
                                            }`}
                                        onClick={() => {
                                            updateData({ type: type.id as ActivityType });
                                            setTimeout(() => {
                                                nextStep();
                                            }, 300);
                                            // nextStep();
                                        }}
                                    >
                                        <CardContent className="p-0">
                                            <div className="relative h-32 overflow-hidden rounded-t-lg">
                                                <img
                                                    src={type.image}
                                                    alt={type.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                                <div className="absolute bottom-2 left-2 text-white">
                                                    <IconComponent className="w-6 h-6" />
                                                </div>
                                            </div>
                                            <div className="p-4">
                                                <h3 className="font-medium mb-1">{type.name}</h3>
                                                <p className="text-sm text-muted-foreground">{type.description}</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Activity Details</h2>
                            <p className="text-muted-foreground">Give your activity a catchy title and description</p>
                        </div>

                        {selectedActivityType && (
                            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                                <div className={`w-10 h-10 ${selectedActivityType.color} rounded-lg flex items-center justify-center text-white`}>
                                    {React.createElement(selectedActivityType.icon, { className: "w-5 h-5" })}
                                </div>
                                <div>
                                    <p className="font-medium">{selectedActivityType.name}</p>
                                    <p className="text-sm text-muted-foreground">{selectedActivityType.description}</p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="title">Activity Title</Label>
                                <Input
                                    id="title"
                                    placeholder="e.g., Morning Tennis Tournament, Beginner Yoga Flow"
                                    value={activityData.title}
                                    onChange={(e) => updateData({ title: e.target.value })}
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    placeholder="Describe what participants can expect, what to bring, skill level, etc."
                                    value={activityData.description}
                                    onChange={(e) => updateData({ description: e.target.value })}
                                    rows={4}
                                    className="mt-1"
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    {activityData.description.length}/500 characters
                                </p>
                            </div>

                            <div className="mt-4">
                                <ImageUpload
                                    onUpload={handleActivityImageUpload}
                                    onSuccess={handleActivityImageUploadSuccess}
                                    maxSize={5}
                                    preview={true}
                                    multiple={false}
                                    accept="image/*"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Schedule Your Activity</h2>
                            <p className="text-muted-foreground">Choose when your activity will take place</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <Label>Select Date</Label>
                                {/* <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start mt-1"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {activityData.date ? activityData.date.toLocaleDateString() : 'Pick a date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={activityData.date}
                                            onSelect={(date) => updateData({ date })}
                                            disabled={(date) => date < new Date()}
                                        />
                                    </PopoverContent>
                                </Popover> */}
                                <Input
                                    id="date"
                                    type="date"
                                    value={activityData.date}
                                    onChange={(e) => updateData({ date: e.target.value })}
                                    min={currentDate}
                                    className={activityData.date ? 'border-destructive' : ''}
                                />
                            </div>

                            <div>
                                <Label>Duration (minutes)</Label>
                                <div className="mt-1">
                                    <Slider
                                        value={[activityData.durationMinutes ?? 0]}
                                        onValueChange={(value) => updateData({ durationMinutes: value[0] })}
                                        max={600}
                                        min={15}
                                        step={15}
                                        className="mb-2"
                                    />
                                    <p className="text-sm text-center text-muted-foreground">
                                        {activityData.durationMinutes} minute{activityData.durationMinutes !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label>Select Time</Label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                                {quickTimeSlots.map((slot) => (
                                    <Button
                                        key={slot.time}
                                        variant={activityData.time === slot.time ? "default" : "outline"}
                                        onClick={() => updateData({ time: slot.time })}
                                        className="flex flex-col h-auto p-3"
                                    >
                                        <Clock className="w-4 h-4 mb-1" />
                                        <span className="text-sm">{slot.label}</span>
                                        <span className="text-xs opacity-70">
                                            {parseInt(slot.time.split(':')[0]) > 12
                                                ? `${parseInt(slot.time.split(':')[0]) - 12}:${slot.time.split(':')[1]} PM`
                                                : `${slot.time} AM`
                                            }
                                        </span>
                                    </Button>
                                ))}
                            </div>

                            <div className="mt-4">
                                <Label htmlFor="custom-time">Or enter custom time</Label>
                                <Input
                                    id="custom-time"
                                    type="time"
                                    value={activityData.time}
                                    onChange={(e) => updateData({ time: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Choose Location</h2>
                            <p className="text-muted-foreground">Where will your activity take place?</p>
                        </div>

                        <LocationPicker
                            onLocationSelect={handleLocationSelect}
                            selectedLocation={selectedLocationFeature || undefined}
                            searchOptions={{
                                limit: 5,
                                types: ['poi', 'address'], // Focus on points of interest and addresses
                                country: ['us'],
                            }}
                            title="Activity Location"
                            description="Search for where your activity will take place and preview it on the map"
                            showMapPreview={true}
                            mapHeight={250}
                        />

                        {/* Location selection feedback */}
                        {selectedLocationFeature && (
                            <div className="flex items-center justify-center p-3 bg-green-50 border border-green-200 rounded-lg">
                                <MapPin className="w-4 h-4 text-green-600 mr-2" />
                                <span className="text-sm text-green-700">
                                    Location selected: {selectedLocationFeature.place_name}
                                </span>
                            </div>
                        )}

                        {/* Alternative: Popular Locations for quick selection */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-background px-2 text-muted-foreground">or choose from popular</span>
                            </div>
                        </div>

                        {/* Popular Locations */}
                        <div>
                            <Label>Popular Locations</Label>
                            <div className="grid grid-cols-1 gap-3 mt-2">
                                {popularLocations.map((loc, index) => (
                                    <Card
                                        key={index}
                                        className={`cursor-pointer transition-all duration-200 hover:shadow-md ${activityData.locationDisplayName === `${loc.name}, ${loc.address}` ? 'ring-2 ring-primary' : ''
                                            }`}
                                        onClick={() => {
                                            // Create a mock LocationFeature for popular locations
                                            const mockFeature: LocationFeature = {
                                                id: `popular-${index}`,
                                                type: 'Feature',
                                                place_name: `${loc.name}, ${loc.address}`,
                                                text: loc.name,
                                                center: [loc.coordinates[0], loc.coordinates[1]],
                                                geometry: {
                                                    type: 'Point',
                                                    coordinates: [loc.coordinates[0], loc.coordinates[1]],
                                                },
                                                properties: {
                                                    category: loc.type,
                                                },
                                            };
                                            handleLocationSelect(mockFeature);
                                        }}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                                    <div>
                                                        <p className="font-medium">{loc.name}</p>
                                                        <p className="text-sm text-muted-foreground">{loc.address}</p>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className="text-xs">
                                                    {loc.type}
                                                </Badge>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Activity Details</h2>
                            <p className="text-muted-foreground">Set participant limits and difficulty level</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <Label>Maximum Participants</Label>
                                <div className="mt-1">
                                    <Slider
                                        value={[activityData.maxParticipants]}
                                        onValueChange={(value) => updateData({ maxParticipants: value[0] })}
                                        max={50}
                                        min={1}
                                        step={1}
                                        className="mb-2"
                                    />
                                    <p className="text-sm text-center text-muted-foreground">
                                        {activityData.maxParticipants} participant{activityData.maxParticipants !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label>Difficulty Level</Label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                                {difficultyLevels.map((level) => (
                                    <Card
                                        key={level.id}
                                        className={`cursor-pointer transition-all duration-200 hover:shadow-md ${activityData.difficulty === level.id ? 'ring-2 ring-primary' : ''
                                            }`}
                                        onClick={() => updateData({ difficulty: level.id as ActivityDifficulty })}
                                    >
                                        <CardContent className="p-4 text-center">
                                            <Badge className={`${level.color} mb-2`}>
                                                {level.name}
                                            </Badge>
                                            <p className="text-xs text-muted-foreground">{level.description}</p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Equipment Provided</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Will you provide equipment for participants?
                                    </p>
                                </div>
                                {/* <Switch
                                    checked={activityData.equipmentProvided}
                                    onCheckedChange={(checked) => updateData({ equipmentProvided: checked })}
                                /> */}
                            </div>

                            {/* <div>
                                <Label htmlFor="requirements">Special Requirements</Label>
                                <Textarea
                                    id="requirements"
                                    placeholder="Any special requirements, what to bring, etc."
                                    value={activityData.requirements}
                                    onChange={(e) => updateData({ requirements: e.target.value })}
                                    rows={3}
                                    className="mt-1"
                                />
                            </div> */}
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Set Your Price</h2>
                            <p className="text-muted-foreground">How much will participants pay?</p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                                <div className="space-y-0.5">
                                    <Label>Free Activity</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Make this activity free for everyone
                                    </p>
                                </div>
                                <Switch
                                    checked={activityData.isFree}
                                    onCheckedChange={(checked) => updateData({ isFree: checked, price: checked ? 0 : activityData.price })}
                                />
                            </div>

                            {!activityData.isFree && (
                                <div>
                                    <Label>Price per participant</Label>
                                    <div className="relative mt-1">
                                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={activityData.price}
                                            onChange={(e) => updateData({ price: parseFloat(e.target.value) || 0 })}
                                            className="pl-10"
                                        />
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Set a fair price that covers your costs and time
                                    </p>
                                </div>
                            )}

                            {!activityData.isFree && activityData.price > 0 && (
                                <Card className="bg-blue-50 border-blue-200">
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <DollarSign className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="font-medium text-blue-900">Pricing Summary</p>
                                                <p className="text-sm text-blue-700">
                                                    Participants will pay ${activityData.price} each
                                                </p>
                                                {activityData.maxParticipants > 0 && (
                                                    <p className="text-sm text-blue-700">
                                                        Maximum revenue: ${(activityData.price * activityData.maxParticipants).toFixed(2)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                );

            case 7:
                return (
                    <div className="space-y-6 max-w-2xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl font-semibold mb-2">Review Your Activity</h2>
                            <p className="text-muted-foreground">Everything looks good? Let's create it!</p>
                        </div>

                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    {selectedActivityType && (
                                        <div className={`w-12 h-12 ${selectedActivityType.color} rounded-lg flex items-center justify-center text-white`}>
                                            {React.createElement(selectedActivityType.icon, { className: "w-6 h-6" })}
                                        </div>
                                    )}
                                    <div>
                                        <CardTitle>{activityData.title}</CardTitle>
                                        <p className="text-sm text-muted-foreground">{selectedActivityType?.name}</p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                                        <span>{activityData.date}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        <span>{activityData.time} ({activityData.duration}h)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-muted-foreground" />
                                        <span className="truncate">{selectedLocationFeature?.place_name.split(', ')[0]}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        <span>{activityData.maxParticipants} participants</span>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm font-medium mb-1">Description</p>
                                    <p className="text-sm text-muted-foreground">{activityData.description}</p>
                                </div>

                                <div className="flex items-center justify-between pt-2 border-t">
                                    <div className="flex items-center gap-2">
                                        <Badge className={difficultyLevels.find(d => d.id === activityData.difficulty)?.color}>
                                            {difficultyLevels.find(d => d.id === activityData.difficulty)?.name}
                                        </Badge>
                                        {/* {activityData.equipmentProvided && (
                                            <Badge variant="outline">Equipment Provided</Badge>
                                        )} */}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium">
                                            {activityData.isFree ? 'Free' : `$${activityData.price}`}
                                        </p>
                                        {!activityData.isFree && (
                                            <p className="text-sm text-muted-foreground">per participant</p>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Button
                            onClick={handleCreate}
                            disabled={isCreating}
                            className="w-full h-12"
                        >
                            {isCreating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                    Creating Activity...
                                </>
                            ) : (
                                <>
                                    <Zap className="w-4 h-4 mr-2" />
                                    Create Activity
                                </>
                            )}
                        </Button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <div>
                            <h1 className="text-2xl font-semibold">Create New Activity</h1>
                            <p className="text-muted-foreground">Step {currentStep} of {steps.length}</p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                    >
                        <X className="w-4 h-4" />
                    </Button>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        {steps.map((step) => (
                            <div key={step.id} className="flex flex-col items-center flex-1">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${step.id <= currentStep
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground'
                                    }`}>
                                    {step.id < currentStep ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        step.id
                                    )}
                                </div>
                                <div className="text-center mt-2 hidden sm:block">
                                    <p className="text-xs font-medium">{step.title}</p>
                                    <p className="text-xs text-muted-foreground">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                        <motion.div
                            className="bg-primary h-2 rounded-full"
                            initial={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                            animate={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        className="mb-8"
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                {currentStep < 7 && (
                    <div className="flex justify-between">
                        <Button
                            variant="outline"
                            onClick={prevStep}
                            disabled={currentStep === 1}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Previous
                        </Button>

                        <Button
                            onClick={nextStep}
                            disabled={!canProceed()}
                        >
                            Next
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}